import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ChevronRight, 
  ChevronLeft, 
  X, 
  Sparkles,
  Target,
  Zap,
  Heart,
  Star,
  Trophy,
  Gift
} from "lucide-react";

const TUTORIAL_KEY = 'fruitCrush_tutorialComplete';

// Tutorial steps with visuals and explanations
const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Fruit Crush! 🍓',
    description: 'Get ready for a fruity adventure! Let\'s learn how to play and become a fruit-crushing champion.',
    icon: Sparkles,
    color: 'from-purple-500 to-pink-500',
    visual: (
      <div className="flex justify-center gap-4 text-5xl animate-bounce">
        <span style={{ animationDelay: '0ms' }}>🍎</span>
        <span style={{ animationDelay: '100ms' }}>🍊</span>
        <span style={{ animationDelay: '200ms' }}>🍋</span>
        <span style={{ animationDelay: '300ms' }}>🍇</span>
        <span style={{ animationDelay: '400ms' }}>🍓</span>
      </div>
    )
  },
  {
    id: 'matching',
    title: 'Match 3 or More',
    description: 'Swap adjacent fruits to create matches of 3 or more identical fruits in a row or column. The matched fruits will disappear and you\'ll earn points!',
    icon: Target,
    color: 'from-green-500 to-emerald-500',
    visual: (
      <div className="relative">
        <div className="grid grid-cols-3 gap-2 w-fit mx-auto">
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl">🍎</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl border-2 border-yellow-400 animate-pulse">🍊</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl">🍇</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl border-2 border-yellow-400 animate-pulse">🍊</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl border-2 border-yellow-400 animate-pulse">🍊</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl border-2 border-yellow-400 animate-pulse">🍊</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl">🍋</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl">🍓</div>
          <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center text-2xl">🍎</div>
        </div>
        <div className="absolute -right-2 top-1/2 -translate-y-1/2 text-yellow-400 animate-pulse">
          <ChevronRight className="w-8 h-8" />
        </div>
        <p className="text-center text-yellow-400 text-sm mt-3 font-medium">4 oranges in a row = Match!</p>
      </div>
    )
  },
  {
    id: 'powerups',
    title: 'Create Power-ups',
    description: 'Match more fruits for powerful special items!',
    icon: Zap,
    color: 'from-yellow-500 to-orange-500',
    visual: (
      <div className="space-y-4">
        <div className="flex items-center gap-4 bg-slate-800/50 p-3 rounded-lg">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center text-2xl shadow-lg relative overflow-hidden">
            <span className="relative z-10">🍊</span>
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-white/60"></div>
          </div>
          <div className="flex-1">
            <p className="font-bold text-white">Striped Fruit</p>
            <p className="text-slate-400 text-xs">Match 4 in a row → Clears entire row or column</p>
          </div>
        </div>
        <div className="flex items-center gap-4 bg-slate-800/50 p-3 rounded-lg">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center text-2xl shadow-lg">
            💣
          </div>
          <div className="flex-1">
            <p className="font-bold text-white">Wrapped Bomb</p>
            <p className="text-slate-400 text-xs">Match 5 in L/T shape → Explodes in 3x3 area</p>
          </div>
        </div>
        <div className="flex items-center gap-4 bg-slate-800/50 p-3 rounded-lg">
          <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-500 rounded-lg flex items-center justify-center text-2xl shadow-lg animate-spin-slow">
            🌈
          </div>
          <div className="flex-1">
            <p className="font-bold text-white">Disco Ball</p>
            <p className="text-slate-400 text-xs">Match 5 in a row → Clears all of one fruit type!</p>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'objectives',
    title: 'Complete Level Goals',
    description: 'Each level has unique objectives. Check the goal at the top of the screen and complete it before running out of moves!',
    icon: Target,
    color: 'from-blue-500 to-cyan-500',
    visual: (
      <div className="space-y-3">
        <div className="flex items-center gap-3 bg-slate-800/50 p-3 rounded-lg border border-green-500/30">
          <span className="text-2xl">🍎</span>
          <div className="flex-1">
            <p className="text-white font-medium">Collect 30 Apples</p>
            <div className="w-full bg-slate-700 rounded-full h-2 mt-1">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: '60%' }}></div>
            </div>
          </div>
          <span className="text-green-400 font-bold">18/30</span>
        </div>
        <div className="flex items-center gap-3 bg-slate-800/50 p-3 rounded-lg border border-yellow-500/30">
          <span className="text-2xl">🎯</span>
          <div className="flex-1">
            <p className="text-white font-medium">Reach 5000 Points</p>
            <div className="w-full bg-slate-700 rounded-full h-2 mt-1">
              <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '40%' }}></div>
            </div>
          </div>
          <span className="text-yellow-400 font-bold">2000/5000</span>
        </div>
      </div>
    )
  },
  {
    id: 'health',
    title: 'Health System',
    description: 'You have HP (Health Points) that you spend to play levels. Losing a level costs HP, but it regenerates over time. Premium members get +500 bonus HP!',
    icon: Heart,
    color: 'from-red-500 to-pink-500',
    visual: (
      <div className="space-y-4">
        <div className="bg-slate-800/50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-red-500 fill-red-500" />
              <span className="text-white font-bold">Your Health</span>
            </div>
            <span className="text-white font-bold">100/100</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-4">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 h-4 rounded-full" style={{ width: '100%' }}></div>
          </div>
          <p className="text-slate-400 text-xs mt-2">❤️ Regenerates +1 HP every 30 seconds</p>
        </div>
        <div className="flex items-center gap-2 text-amber-400 bg-amber-500/10 p-2 rounded-lg">
          <Star className="w-5 h-5" />
          <span className="text-sm">Premium members get <strong>+500 HP</strong> bonus!</span>
        </div>
      </div>
    )
  },
  {
    id: 'rewards',
    title: 'Earn Rewards',
    description: 'Complete levels to earn Stars ⭐ and Coins 🪙. Use them in the Shop to buy power-ups, upgrades, and cosmetics!',
    icon: Gift,
    color: 'from-amber-500 to-yellow-500',
    visual: (
      <div className="space-y-3">
        <div className="flex justify-center gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-3xl shadow-lg mb-2">
              ⭐
            </div>
            <p className="text-white font-bold">Stars</p>
            <p className="text-slate-400 text-xs">Buy upgrades</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-3xl shadow-lg mb-2">
              🪙
            </div>
            <p className="text-white font-bold">Coins</p>
            <p className="text-slate-400 text-xs">Buy boosters</p>
          </div>
        </div>
        <div className="bg-slate-800/50 p-3 rounded-lg text-center">
          <p className="text-slate-300 text-sm">🎡 <strong>Daily Spin</strong> - Free rewards every day!</p>
        </div>
      </div>
    )
  },
  {
    id: 'ready',
    title: 'You\'re Ready! 🎮',
    description: 'Time to start your fruit-crushing adventure! Good luck and have fun!',
    icon: Trophy,
    color: 'from-purple-500 to-indigo-500',
    visual: (
      <div className="text-center space-y-4">
        <div className="text-6xl animate-bounce">🏆</div>
        <div className="space-y-2">
          <p className="text-white font-medium">Quick Tips:</p>
          <ul className="text-slate-300 text-sm space-y-1">
            <li>• Combine special fruits for mega explosions!</li>
            <li>• Plan your moves - limited moves per level</li>
            <li>• Complete daily challenges for bonus rewards</li>
            <li>• Visit the Shop for helpful power-ups</li>
          </ul>
        </div>
      </div>
    )
  }
];

export function TutorialOverlay({ onComplete, forceShow = false }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if tutorial has been completed
    const tutorialComplete = localStorage.getItem(TUTORIAL_KEY);
    if (!tutorialComplete || forceShow) {
      setIsVisible(true);
    }
  }, [forceShow]);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(TUTORIAL_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem(TUTORIAL_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      data-testid="tutorial-overlay"
    >
      <Card className="bg-slate-900/95 border-slate-700 w-full max-w-lg overflow-hidden shadow-2xl">
        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${step.color} p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <StepIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{step.title}</h2>
                <p className="text-white/70 text-xs">Step {currentStep + 1} of {TUTORIAL_STEPS.length}</p>
              </div>
            </div>
            <button 
              onClick={handleSkip}
              className="p-2 rounded-full hover:bg-white/20 transition-colors"
              data-testid="tutorial-skip"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <CardContent className="p-6">
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {TUTORIAL_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? 'bg-white w-6' 
                    : index < currentStep 
                      ? 'bg-white/60' 
                      : 'bg-slate-600'
                }`}
              />
            ))}
          </div>

          {/* Visual */}
          <div className="mb-6 min-h-[160px] flex items-center justify-center">
            {step.visual}
          </div>

          {/* Description */}
          <p className="text-slate-300 text-center mb-6 leading-relaxed">
            {step.description}
          </p>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="border-slate-600 hover:bg-slate-800 disabled:opacity-30"
              data-testid="tutorial-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <Button
              onClick={handleNext}
              className={`bg-gradient-to-r ${step.color} hover:opacity-90 flex-1 max-w-[200px]`}
              data-testid="tutorial-next"
            >
              {isLastStep ? (
                <>
                  Start Playing!
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Skip link */}
          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="w-full mt-4 text-slate-500 hover:text-slate-300 text-sm transition-colors"
              data-testid="tutorial-skip-text"
            >
              Skip tutorial
            </button>
          )}
        </CardContent>
      </Card>

      {/* Add spin animation */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </div>
  );
}

// Hook to check/reset tutorial status
export function useTutorial() {
  const [hasSeenTutorial, setHasSeenTutorial] = useState(true);

  useEffect(() => {
    const tutorialComplete = localStorage.getItem(TUTORIAL_KEY);
    setHasSeenTutorial(!!tutorialComplete);
  }, []);

  const resetTutorial = () => {
    localStorage.removeItem(TUTORIAL_KEY);
    setHasSeenTutorial(false);
  };

  const markComplete = () => {
    localStorage.setItem(TUTORIAL_KEY, 'true');
    setHasSeenTutorial(true);
  };

  return { hasSeenTutorial, resetTutorial, markComplete };
}

export default TutorialOverlay;
